/**
 * Reporting Service
 *
 * Dashboard data and business analytics.
 */

import apiClient, { APIResponse, PaginatedResponse, unwrapResponse, unwrapPaginatedResponse } from './apiClient';
import { ENDPOINTS } from '../config/api';

// =============================================================================
// Types
// =============================================================================

export interface DashboardOverview {
  // Stock summary
  stock: {
    total_products: number;
    total_value: number;
    critical_items: number;
    machines_available: number;
    machines_rented: number;
    machines_loa: number;
    machines_maintenance: number;
  };

  // Sales summary
  sales: {
    quotes_pending: number;
    quotes_value: number;
    invoices_unpaid: number;
    invoices_unpaid_value: number;
    revenue_month: number;
    revenue_year: number;
  };

  // Contracts summary
  contracts: {
    rentals_active: number;
    loa_active: number;
    loa_in_default: number;
    expiring_soon: number;
  };

  // Client summary
  clients: {
    total_clients: number;
    active_clients: number;
    new_this_month: number;
  };
}

export interface RevenueData {
  period: string;
  revenue: number;
  expenses: number;
  profit: number;
}

export interface CategoryDistribution {
  category: string;
  count: number;
  value: number;
  percentage: number;
}

export interface ClientDistribution {
  type: string;
  count: number;
  percentage: number;
}

export interface ProvenanceDistribution {
  provenance: string;
  count: number;
  percentage: number;
}

export interface TopProduct {
  product_id: string;
  product_reference: string;
  product_designation: string;
  quantity_sold: number;
  revenue: number;
}

export interface TopClient {
  client_id: string;
  client_nom: string;
  total_invoiced: number;
  total_paid: number;
  contracts_count: number;
}

export interface OverdueInvoice {
  invoice_id: string;
  invoice_code: string;
  client_id: string;
  client_nom: string;
  montant_ttc: number;
  solde_restant: number;
  date_echeance: string;
  jours_retard: number;
}

export interface StockValuationReport {
  site_id: string;
  site_nom: string;
  categories: {
    category_id: string;
    category_nom: string;
    quantity: number;
    value: number;
  }[];
  total_quantity: number;
  total_value: number;
}

export interface ContractExpirationReport {
  contract_id: string;
  contract_code: string;
  type: 'LOCATION' | 'LOA';
  client_id: string;
  client_nom: string;
  machine_designation: string;
  date_fin_prevue: string;
  jours_restants: number;
}

export interface ReportPeriod {
  start_date: string;
  end_date: string;
  period_type: 'day' | 'week' | 'month' | 'quarter' | 'year';
}

// Matches backend KPIFinancierJour schema
export interface KPIFinancierJour {
  date_jour: string;
  site_id?: string;
  site_nom?: string;
  ca_jour_ht: number;
  ca_jour_ttc: number;
  ca_cumul_mois_ht: number;
  ca_cumul_mois_ttc: number;
  ca_cumul_annee_ht: number;
  ca_cumul_annee_ttc: number;
  montant_impayes: number;
  nombre_factures: number;
  nombre_factures_impayees: number;
  marge_brute_jour: number;
  cout_revient_jour: number;
  taux_marge_jour: number | null;
}

// Matches backend KPIDashboard schema (returned by GET /dashboard/kpis)
export interface KPIDashboard {
  date_debut: string;
  date_fin: string;
  periode: string;
  ca_total_ht: number;
  ca_total_ttc: number;
  ca_cumul_annee_ht: number;
  montant_total_impayes: number;
  nombre_factures: number;
  nombre_factures_impayees: number;
  marge_brute_totale: number;
  taux_marge_moyen: number | null;
  evolution_ca_percent: number | null;
  evolution_marge_percent: number | null;
  valeur_stock_totale: number;
  produits_stock_bas: number;
  produits_rupture: number;
  kpis_journaliers: KPIFinancierJour[];
}

// =============================================================================
// Reporting Service
// =============================================================================

export const reportingService = {
  // ---------------------------------------------------------------------------
  // Dashboard
  // ---------------------------------------------------------------------------

  async getDashboardOverview(params?: { periode?: string; site_id?: string }): Promise<DashboardOverview> {
    const periodeMap: Record<string, string> = { month: 'mois', quarter: 'trimestre', year: 'annee' };
    const queryParams: Record<string, string> = {};
    if (params?.periode) {
      queryParams.periode = periodeMap[params.periode] ?? params.periode;
    }
    if (params?.site_id) {
      queryParams.site_id = params.site_id;
    }
    const response = await apiClient.get<APIResponse<KPIDashboard>>(
      `${ENDPOINTS.DASHBOARD}/kpis`,
      { params: queryParams }
    );
    const kpi = unwrapResponse(response);

    // Fetch supplementary data in parallel; use allSettled so failures don't break the dashboard
    const [
      dashboardStockResult,
      productStatsResult,
      activeRentalsResult,
      loaActiveResult,
      loaDefaultedResult,
      clientStatsResult,
    ] = await Promise.allSettled([
      // 1. Stock summary from /dashboard/stock
      apiClient.get<APIResponse<{
        stocks: { nombre_produits: number; produits_stock_bas: number }[];
        total_produits: number;
        total_stock_bas: number;
        total_valeur_fifo: number;
      }>>(`${ENDPOINTS.DASHBOARD}/stock`),

      // 2. Product statistics (by_status gives machine counts per status)
      apiClient.get<APIResponse<{
        total: number;
        by_type: Record<string, number>;
        by_status: Record<string, number>;
      }>>(`${ENDPOINTS.PRODUCTS}/statistics`),

      // 3. Active rental contracts count (page_size=1, we only need pagination.total_items)
      apiClient.get<{
        data: unknown[];
        pagination: { total_items: number };
      }>(`${ENDPOINTS.LOCATIONS}/active`, { params: { page: 1, page_size: 1 } }),

      // 4. LOA active contracts count
      apiClient.get<{
        data: unknown[];
        pagination: { total_items: number };
      }>(ENDPOINTS.LOA, { params: { page: 1, page_size: 1, status: 'ACTIVE' } }),

      // 5. LOA defaulted contracts count
      apiClient.get<{
        data: unknown[];
        pagination: { total_items: number };
      }>(ENDPOINTS.LOA, { params: { page: 1, page_size: 1, status: 'DEFAULTED' } }),

      // 6. Client statistics
      apiClient.get<APIResponse<{
        total: number;
        by_type: Record<string, number>;
        by_provenance: Record<string, number>;
      }>>(`${ENDPOINTS.CLIENTS}/statistics`),
    ]);

    // Extract stock data
    let totalProducts = 0;
    let totalStockBas = 0;
    if (dashboardStockResult.status === 'fulfilled') {
      const stockData = dashboardStockResult.value.data.data;
      totalProducts = stockData.total_produits ?? 0;
      totalStockBas = stockData.total_stock_bas ?? 0;
    }

    // Extract product status counts (machines_available, machines_rented, etc.)
    let machinesAvailable = 0;
    let machinesRented = 0;
    let machinesLoa = 0;
    let machinesMaintenance = 0;
    if (productStatsResult.status === 'fulfilled') {
      const byStatus = productStatsResult.value.data.data?.by_status ?? {};
      machinesAvailable = byStatus['AVAILABLE'] ?? 0;
      machinesRented = byStatus['RENTED'] ?? 0;
      machinesLoa = byStatus['LOA'] ?? 0;
      machinesMaintenance = byStatus['MAINTENANCE'] ?? 0;
    }

    // Extract active rentals count
    let rentalsActive = 0;
    if (activeRentalsResult.status === 'fulfilled') {
      rentalsActive = activeRentalsResult.value.data.pagination?.total_items ?? 0;
    }

    // Extract LOA counts
    let loaActive = 0;
    let loaInDefault = 0;
    if (loaActiveResult.status === 'fulfilled') {
      loaActive = loaActiveResult.value.data.pagination?.total_items ?? 0;
    }
    if (loaDefaultedResult.status === 'fulfilled') {
      loaInDefault = loaDefaultedResult.value.data.pagination?.total_items ?? 0;
    }

    // Extract client statistics
    let totalClients = 0;
    let activeClients = 0;
    if (clientStatsResult.status === 'fulfilled') {
      const clientData = clientStatsResult.value.data.data;
      totalClients = clientData?.total ?? 0;
      // Sum by_type values as a proxy for active clients (statistics endpoint only counts non-deleted)
      activeClients = totalClients;
    }

    // Transform backend KPIDashboard → frontend DashboardOverview shape
    return {
      stock: {
        total_products: totalProducts,
        total_value: Number(kpi.valeur_stock_totale),
        critical_items: totalStockBas || kpi.produits_stock_bas,
        machines_available: machinesAvailable,
        machines_rented: machinesRented,
        machines_loa: machinesLoa,
        machines_maintenance: machinesMaintenance,
      },
      sales: {
        quotes_pending: 0,
        quotes_value: 0,
        invoices_unpaid: kpi.nombre_factures_impayees,
        invoices_unpaid_value: Number(kpi.montant_total_impayes),
        revenue_month: Number(kpi.ca_total_ttc),
        revenue_year: Number(kpi.ca_cumul_annee_ht),
      },
      contracts: {
        rentals_active: rentalsActive,
        loa_active: loaActive,
        loa_in_default: loaInDefault,
        expiring_soon: 0,
      },
      clients: {
        total_clients: totalClients,
        active_clients: activeClients,
        new_this_month: 0,
      },
    };
  },

  // Stock-only overview for roles without REPORTING access (e.g. MAGASINIER)
  async getStockOnlyOverview(): Promise<DashboardOverview> {
    const [dashboardStockResult, productStatsResult, activeRentalsResult] = await Promise.allSettled([
      apiClient.get<APIResponse<{
        stocks: { nombre_produits: number; produits_stock_bas: number }[];
        total_produits: number;
        total_stock_bas: number;
        total_valeur_fifo: number;
      }>>(`${ENDPOINTS.DASHBOARD}/stock`),
      apiClient.get<APIResponse<{
        total: number;
        by_type: Record<string, number>;
        by_status: Record<string, number>;
      }>>(`${ENDPOINTS.PRODUCTS}/statistics`),
      apiClient.get<{
        data: unknown[];
        pagination: { total_items: number };
      }>(`${ENDPOINTS.LOCATIONS}/active`, { params: { page: 1, page_size: 1 } }),
    ]);

    let totalProducts = 0, totalStockBas = 0, totalValue = 0;
    if (dashboardStockResult.status === 'fulfilled') {
      const d = dashboardStockResult.value.data.data;
      totalProducts = d.total_produits ?? 0;
      totalStockBas = d.total_stock_bas ?? 0;
      totalValue = d.total_valeur_fifo ?? 0;
    }

    let machinesAvailable = 0, machinesRented = 0, machinesLoa = 0, machinesMaintenance = 0;
    if (productStatsResult.status === 'fulfilled') {
      const byStatus = productStatsResult.value.data.data?.by_status ?? {};
      machinesAvailable = byStatus['AVAILABLE'] ?? 0;
      machinesRented = byStatus['RENTED'] ?? 0;
      machinesLoa = byStatus['LOA'] ?? 0;
      machinesMaintenance = byStatus['MAINTENANCE'] ?? 0;
    }

    let rentalsActive = 0;
    if (activeRentalsResult.status === 'fulfilled') {
      rentalsActive = activeRentalsResult.value.data.pagination?.total_items ?? 0;
    }

    return {
      stock: {
        total_products: totalProducts,
        total_value: totalValue,
        critical_items: totalStockBas,
        machines_available: machinesAvailable,
        machines_rented: machinesRented,
        machines_loa: machinesLoa,
        machines_maintenance: machinesMaintenance,
      },
      sales: { quotes_pending: 0, quotes_value: 0, invoices_unpaid: 0, invoices_unpaid_value: 0, revenue_month: 0, revenue_year: 0 },
      contracts: { rentals_active: rentalsActive, loa_active: 0, loa_in_default: 0, expiring_soon: 0 },
      clients: { total_clients: 0, active_clients: 0, new_this_month: 0 },
    };
  },

  // Used by ReportingModule — calls /dashboard/kpis with a period filter
  async getOverview(periode: 'month' | 'quarter' | 'year' = 'year'): Promise<KPIDashboard> {
    const periodeMap: Record<string, string> = { month: 'mois', quarter: 'trimestre', year: 'annee' };
    const response = await apiClient.get<APIResponse<KPIDashboard>>(
      `${ENDPOINTS.DASHBOARD}/kpis`,
      { params: { periode: periodeMap[periode] ?? periode } }
    );
    return unwrapResponse(response);
  },

  async getRecentActivity(_limit = 20): Promise<{
    id: string;
    timestamp: string;
    user: string;
    action: string;
    details: string;
    entity_type?: string;
    entity_id?: string;
  }[]> {
    // No dedicated activity endpoint
    return [];
  },

  // ---------------------------------------------------------------------------
  // Revenue Reports
  // ---------------------------------------------------------------------------

  async getRevenueReport(_period: ReportPeriod): Promise<RevenueData[]> {
    // No dedicated revenue endpoint - derive from KPIs
    return [];
  },

  async getMonthlyRevenue(year: number): Promise<RevenueData[]> {
    try {
      const response = await apiClient.get<APIResponse<KPIDashboard>>(
        `${ENDPOINTS.DASHBOARD}/kpis`,
        { params: { periode: 'annee' } }
      );
      const data = unwrapResponse(response);
      const kpis = data.kpis_journaliers || [];
      // Aggregate daily KPIs into monthly buckets
      const monthlyMap: Record<number, { revenue: number; expenses: number }> = {};
      for (let m = 0; m < 12; m++) monthlyMap[m] = { revenue: 0, expenses: 0 };
      kpis.forEach((kpi: any) => {
        const d = new Date(kpi.date_jour);
        if (d.getFullYear() === year) {
          const m = d.getMonth();
          monthlyMap[m].revenue += Number(kpi.ca_jour_ttc || 0);
          monthlyMap[m].expenses += Number(kpi.cout_revient_jour || 0);
        }
      });
      return Object.entries(monthlyMap).map(([m, vals]) => ({
        period: String(Number(m) + 1),
        revenue: vals.revenue,
        expenses: vals.expenses,
        profit: vals.revenue - vals.expenses,
      }));
    } catch {
      return [];
    }
  },

  // ---------------------------------------------------------------------------
  // Stock Reports
  // ---------------------------------------------------------------------------

  async getStockValuationReport(): Promise<StockValuationReport[]> {
    // Use dashboard/stock endpoint as source
    try {
      const response = await apiClient.get<APIResponse<{
        stocks: { site_id: string; site_nom: string; nombre_produits: number; valeur_fifo: number }[];
      }>>(`${ENDPOINTS.DASHBOARD}/stock`);
      const stockData = unwrapResponse(response);
      return (stockData.stocks || []).map((s: { site_id: string; site_nom: string; nombre_produits: number; valeur_fifo: number }) => ({
        site_id: s.site_id,
        site_nom: s.site_nom,
        categories: [],
        total_quantity: s.nombre_produits,
        total_value: s.valeur_fifo,
      }));
    } catch {
      return [];
    }
  },

  async getStockMovementsReport(_period: ReportPeriod): Promise<{
    entries: number;
    exits: number;
    transfers: number;
    adjustments: number;
    total_value_in: number;
    total_value_out: number;
  }> {
    // No dedicated stock movements report endpoint
    return { entries: 0, exits: 0, transfers: 0, adjustments: 0, total_value_in: 0, total_value_out: 0 };
  },

  async getCategoryDistribution(): Promise<CategoryDistribution[]> {
    // No dedicated category distribution endpoint
    return [];
  },

  // ---------------------------------------------------------------------------
  // Client Reports
  // ---------------------------------------------------------------------------

  async getClientDistribution(): Promise<ClientDistribution[]> {
    // Derive from client statistics endpoint
    try {
      const response = await apiClient.get<APIResponse<{
        total: number;
        by_type: Record<string, number>;
      }>>(`${ENDPOINTS.CLIENTS}/statistics`);
      const stats = unwrapResponse(response);
      const total = stats.total || 1;
      return Object.entries(stats.by_type || {}).map(([type, count]) => ({
        type,
        count: count as number,
        percentage: Math.round(((count as number) / total) * 100),
      }));
    } catch {
      return [];
    }
  },

  async getProvenanceDistribution(): Promise<ProvenanceDistribution[]> {
    // Try reporting endpoint first (accessible to COMPTABLE), fallback to client statistics
    try {
      const now = new Date();
      const response = await apiClient.get<APIResponse<{
        provenances: { provenance_nom: string; ca_ttc: number; quantite_totale: number }[];
      }>>(`${ENDPOINTS.REPORTING}/ca-par-provenance`, {
        params: { annee: now.getFullYear(), mois: now.getMonth() + 1 },
      });
      const data = unwrapResponse(response);
      const provenances = data.provenances || [];
      if (provenances.length > 0) {
        const totalCA = provenances.reduce((sum: number, p: any) => sum + Number(p.ca_ttc || 0), 0) || 1;
        return provenances.map((p: any) => ({
          provenance: p.provenance_nom || 'Inconnu',
          count: Number(p.quantite_totale || 0),
          percentage: Math.round((Number(p.ca_ttc || 0) / totalCA) * 100),
        }));
      }
      // Fallback: use client statistics for count-based distribution
      const statsResponse = await apiClient.get<APIResponse<{
        total: number;
        by_provenance: Record<string, number>;
      }>>(`${ENDPOINTS.CLIENTS}/statistics`);
      const stats = unwrapResponse(statsResponse);
      const total = stats.total || 1;
      return Object.entries(stats.by_provenance || {}).map(([provenance, count]) => ({
        provenance,
        count: count as number,
        percentage: Math.round(((count as number) / total) * 100),
      }));
    } catch {
      return [];
    }
  },

  async getTopClients(_limit = 10, _period?: ReportPeriod): Promise<TopClient[]> {
    // No dedicated top clients endpoint
    return [];
  },

  // ---------------------------------------------------------------------------
  // Sales Reports
  // ---------------------------------------------------------------------------

  async getTopProducts(_limit = 10, _period?: ReportPeriod): Promise<TopProduct[]> {
    // No dedicated top products endpoint
    return [];
  },

  async getOverdueInvoices(): Promise<OverdueInvoice[]> {
    // Use dedicated unpaid invoices endpoint
    try {
      const response = await apiClient.get<{ data: OverdueInvoice[] }>(
        `${ENDPOINTS.INVOICES}/unpaid`,
        { params: { page: 1, page_size: 50 } }
      );
      return response.data?.data || [];
    } catch {
      return [];
    }
  },

  async getSalesStats(_period?: ReportPeriod): Promise<{
    total_quotes: number;
    quotes_converted: number;
    conversion_rate: number;
    total_invoiced: number;
    total_paid: number;
    collection_rate: number;
  }> {
    // No dedicated sales stats endpoint
    return {
      total_quotes: 0,
      quotes_converted: 0,
      conversion_rate: 0,
      total_invoiced: 0,
      total_paid: 0,
      collection_rate: 0,
    };
  },

  // ---------------------------------------------------------------------------
  // Contract Reports
  // ---------------------------------------------------------------------------

  async getExpiringContracts(daysAhead = 30): Promise<ContractExpirationReport[]> {
    // Use the ending-soon endpoint from locations
    try {
      const response = await apiClient.get<PaginatedResponse<{
        id: string;
        code: string;
        client_nom: string;
        machine_nom: string;
        date_fin_prevue: string;
      }>>(`${ENDPOINTS.LOCATIONS}/ending-soon`, {
        params: { days: daysAhead },
      });
      const contracts = (response.data?.data || []);
      const today = new Date();
      return contracts.map((c: { id: string; code: string; client_nom: string; machine_nom: string; date_fin_prevue: string }) => ({
        contract_id: c.id,
        contract_code: c.code,
        type: 'LOCATION' as const,
        client_id: '',
        client_nom: c.client_nom || '',
        machine_designation: c.machine_nom || '',
        date_fin_prevue: c.date_fin_prevue,
        jours_restants: Math.ceil((new Date(c.date_fin_prevue).getTime() - today.getTime()) / (1000 * 60 * 60 * 24)),
      }));
    } catch {
      return [];
    }
  },

  async getLOADefaultReport(): Promise<{
    total_in_default: number;
    total_amount_overdue: number;
    contracts: {
      contract_id: string;
      contract_code: string;
      client_nom: string;
      nombre_impayes: number;
      montant_impaye: number;
      jours_retard_max: number;
    }[];
  }> {
    // Use LOA with-overdue endpoint
    try {
      const response = await apiClient.get<PaginatedResponse<{
        id: string;
        code: string;
        client_nom: string;
        nombre_echeances_retard: number;
      }>>(`${ENDPOINTS.LOA}/with-overdue`);
      const contracts = response.data?.data || [];
      return {
        total_in_default: contracts.length,
        total_amount_overdue: 0,
        contracts: contracts.map((c: { id: string; code: string; client_nom: string; nombre_echeances_retard: number }) => ({
          contract_id: c.id,
          contract_code: c.code,
          client_nom: c.client_nom || '',
          nombre_impayes: c.nombre_echeances_retard || 0,
          montant_impaye: 0,
          jours_retard_max: 0,
        })),
      };
    } catch {
      return { total_in_default: 0, total_amount_overdue: 0, contracts: [] };
    }
  },

  // ---------------------------------------------------------------------------
  // Export
  // ---------------------------------------------------------------------------

  async exportReport(
    reportType: 'stock' | 'sales' | 'clients' | 'contracts' | 'dashboard' | string,
    format: 'csv' | 'xlsx' | 'pdf' | string,
    period?: ReportPeriod | Record<string, unknown>
  ): Promise<Blob> {
    const response = await apiClient.get(`${ENDPOINTS.REPORTING}/exports/download`, {
      params: { report_type: reportType, format, ...period },
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * Helper to download exported report
   */
  async downloadReport(
    reportType: 'stock' | 'sales' | 'clients' | 'contracts',
    format: 'csv' | 'xlsx' | 'pdf',
    period?: ReportPeriod
  ): Promise<void> {
    const blob = await this.exportReport(reportType, format, period);
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${reportType}_report_${new Date().toISOString().split('T')[0]}.${format}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  },
};

export default reportingService;
